import { z } from "zod";

import * as DbEnum from "../../../../generated/db-enum";
import { unlistedDiscriminantSchema } from "../../../../shared/core/discriminated-union-read.schema";
import {
  VIBE_SHIP_GATE_MODE_DEFAULT,
  VibeShipGateModeReadSchema
} from "../../../../shared/domain/vibe/ship-gate-mode";
import { VibeFailureReasonCodeReadSchema } from "../../../../shared/domain/vibe/vibe-failure-reason-code";
import { VibeApprovalRequestSchema } from "./approvals.schemas";
import { VibeBuildWaitReadSchema } from "./build-wait.schemas";
import {
  VibeBuilderKindReadSchema,
  VibeDeploymentColorReadSchema,
  VibeTriggerSourceReadSchema
} from "./enum-read.schemas";
import {
  VibeBuildJobStatusReadSchema,
  VibeCostSafetyStatusReadSchema,
  VibeDeploymentStatusReadSchema
} from "./status-read.schemas";

/**
 * Lifecycle of a Vibe deployment, derived from the generated
 * `VIBE_DEPLOYMENT_STATUS_VALUES` tuple so the wire contract cannot drift from
 * the database.
 *
 * Derive rather than hand-roll. `z.nativeEnum` over a Prisma client enum would
 * pull the Prisma runtime into the frontend bundle, but this tuple is plain
 * generated string literals with no imports, so it costs nothing — the same
 * reason `VibeBuilderKindSchema` and `VibeTriggerSourceSchema` below derive
 * from it too.
 *
 * This is the EMITTING side — what the database can hold. A response carries
 * `VibeDeploymentStatusReadSchema`, the hand-listed reader vocabulary that must
 * lead this one; `status-read.schemas.ts` says why the two are separate.
 */
export const VibeDeploymentStatusSchema = z.enum(DbEnum.VIBE_DEPLOYMENT_STATUS_VALUES);
export type VibeDeploymentStatusValue = z.infer<typeof VibeDeploymentStatusSchema>;

/**
 * The internal blue/green slot a deployment occupies, derived from the
 * generated `VIBE_DEPLOYMENT_COLOR_VALUES` tuple for the same reason
 * `VibeDeploymentStatusSchema` above is — a hand-listed pair cannot grow when
 * the Prisma enum does, and nothing errors when it fails to.
 */
export const VibeDeploymentColorSchema = z.enum(DbEnum.VIBE_DEPLOYMENT_COLOR_VALUES);
export type VibeDeploymentColorValue = z.infer<typeof VibeDeploymentColorSchema>;

/**
 * Lifecycle of the build job attached to a deployment, bound to the generated
 * `VIBE_BUILD_JOB_STATUS_VALUES` tuple so a new Prisma status reaches the wire
 * contract instead of being silently rejected by a stale literal union.
 *
 * The EMITTING side, like `VibeDeploymentStatusSchema` above: a response carries
 * `VibeBuildJobStatusReadSchema`.
 */
export const VibeBuildJobStatusSchema = z.enum(DbEnum.VIBE_BUILD_JOB_STATUS_VALUES);
export type VibeBuildJobStatusValue = z.infer<typeof VibeBuildJobStatusSchema>;

/**
 * Bound to the generated `VIBE_BUILDER_KIND_VALUES` array (same drift-safe
 * bridge as `VibeTriggerSourceSchema` below) — a new Prisma builder kind
 * flows into this union automatically. Shared by deployments, build jobs,
 * and the template catalog (`templates.schemas.ts`).
 */
export const VibeBuilderKindSchema = z.enum(DbEnum.VIBE_BUILDER_KIND_VALUES);
export type VibeBuilderKindValue = z.infer<typeof VibeBuilderKindSchema>;

/**
 * What kicked off a deployment. Bound to the generated `VIBE_TRIGGER_SOURCE_VALUES`
 * bridge — a plain `as const` string tuple (no Prisma runtime, frontend-safe) —
 * so a new Prisma enum value flows into this union automatically, keeping the
 * DTO and the frontend's exhaustive label Record honest.
 */
export const VibeTriggerSourceSchema = z.enum(DbEnum.VIBE_TRIGGER_SOURCE_VALUES);
export type VibeTriggerSourceValue = z.infer<typeof VibeTriggerSourceSchema>;

/**
 * Commit sha the deploy was triggered against. Accepts the short (7) and
 * full (40) hex forms — `nexus vibe deploy --sha 1a2b3c4` is a documented
 * ergonomic, and the rollback lever deliberately names an OLD commit that no
 * ref points at any more.
 *
 * Lowercased here, not merely validated case-insensitively. The sha is not
 * only checked, it is PERSISTED as `VibeDeployment.triggerSha` and composed
 * into the ECR image tag — so `1A2B3C4` and `1a2b3c4` are the same commit but
 * would produce two different tags and two different builds. The regex used to
 * carry `/i` with no transform, which made that reachable from the API.
 *
 * LENGTH is not normalized here and cannot be: expanding an abbreviation needs
 * the repository. `TriggerVibeDeploymentUseCase` expands it against the app's
 * known refs, which is why the persisted sha is full-length whenever the
 * abbreviation names a ref head.
 */
export const VibeTriggerShaSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(7, "sha must be at least 7 characters")
  .max(40, "sha must be at most 40 characters")
  .regex(/^[0-9a-f]+$/, "sha must be hexadecimal");

export const VibeBuildJobSchema = z.object({
  id: z.string().uuid(),
  vibeDeploymentId: z.string().uuid(),
  organizationId: z.string(),
  status: VibeBuildJobStatusReadSchema,
  /// Which build strategy the executor actually used, learned from its
  /// terminal report. Null until it reports one — a build still PENDING or
  /// RUNNING, one that timed out without ever reporting, or a report that
  /// carried no builder. It is never a request: the caller cannot pick the
  /// builder, the executor decides over a checkout the control plane never
  /// sees (its own Dockerfile → one we generate → Nixpacks). Read leniently: a
  /// strategy this contract does not list reads as unlisted, never as a failed
  /// response.
  builder: VibeBuilderKindReadSchema.nullable(),
  /// Empty string until the runner opens the log stream.
  logsRef: z.string(),
  /// Null until the build reaches a terminal state.
  durationMs: z.number().int().nonnegative().nullable(),
  errorReason: z.string().nullable(),
  /// Why the build ended, as a structured code — see `VibeFailureReasonCode`.
  /// The only field a surface may classify a failure on; `errorReason` is the
  /// human detail. Lenient: absent (an older backend) or unknown reads as null.
  reasonCode: VibeFailureReasonCodeReadSchema,
  /// Why the build has not started, derived at read time — see
  /// `VibeBuildWaitSchema`. Read leniently: a reason this contract does not
  /// list reads as `unlisted` rather than failing the response. Null once the build is admitted or has ended, and
  /// null for a queued build whose deployment has already left BUILDING, which
  /// the runner will never pick up: that deployment's own status says why.
  waiting: VibeBuildWaitReadSchema.nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});

export type VibeBuildJobDto = z.infer<typeof VibeBuildJobSchema>;

/**
 * The newer deployment that took a DISPLACED deployment's place — enough to
 * name it (`v8 · 1a2b3c4`) and link to it without a second read.
 *
 * Structured on purpose. The same fact is also spelled inside `errorReason`,
 * but that is prose for a human, and a client that parses it back out has a
 * second definition of the sentence that drifts the first time anyone rewords
 * it.
 */
export const VibeDeploymentDisplacerSchema = z.object({
  id: z.string().uuid(),
  versionNumber: z.number().int(),
  triggerSha: z.string()
});

export type VibeDeploymentDisplacerDto = z.infer<typeof VibeDeploymentDisplacerSchema>;

export const VibeDeploymentSchema = z.object({
  id: z.string().uuid(),
  vibeAppId: z.string().uuid(),
  organizationId: z.string(),
  /// Read leniently — no surface renders the slot, so an unlisted one must not
  /// fail the read carrying everything that IS rendered.
  color: VibeDeploymentColorReadSchema,
  /// Monotonic per-app version — the user-facing `v{n}` (v1, v2, …). This is
  /// what the console/CLI render; `color` is the internal blue/green slot and
  /// is not surfaced to users.
  versionNumber: z.number().int(),
  status: VibeDeploymentStatusReadSchema,
  triggerSha: z.string(),
  /// What kicked off this deployment — a deploy-branch push, a CLI deploy or
  /// the console. Read leniently: a source this contract does not list reads as
  /// unlisted, and renders as the server's own word.
  triggerSource: VibeTriggerSourceReadSchema,
  /// Empty string until the build job advances to SUCCEEDED.
  imageRef: z.string(),
  /// The port the BUILD observed this image listening on, stamped alongside
  /// `imageRef`. Null means NOT OBSERVED — never "no port", and never "8080":
  /// the platform default is applied downstream by `vibeAppContainerPort`, and
  /// conflating the two here would make an un-detected build indistinguishable
  /// from one that detected the default. Every deployment predating detection
  /// reads null.
  ///
  /// Surfaced because it is the only way to answer "why is my app being probed
  /// on this port". The resolved answer is `healthCheckConfig.port ?? this ??
  /// 8080`, so a reader needs this value to reconstruct the decision — without
  /// it the port the build chose is invisible even to the operator whose app
  /// is failing its health check because of it.
  detectedPort: z.number().int().min(1).max(65535).nullable(),
  /// Whether this deploy asked for the sha to be BUILT AGAIN rather than
  /// reusing the image already in the registry. Visible because it explains
  /// the `-v{n}` suffix on `imageRef`: a tag that does not match the plain
  /// `<app>-<sha>` form is not a corruption, it is a forced rebuild.
  forceRebuild: z.boolean(),
  /// How hard the ship gate applied to THIS deployment, frozen at trigger time
  /// from the app's `shipGateMode` and the caller's `--skip-verification`
  /// override. Surfaced because it is the only way to tell a deploy that was
  /// checked from one that was waved through: the app's current setting cannot
  /// answer it, since an admin may have changed it since this row was created.
  ///
  /// `.default("OFF")` for the deploy-skew reason documented on
  /// `VibeAppSchema.shipGateMode`: a backend one release behind omits the key,
  /// and a required key would fail the whole deployments list rather than one
  /// field of it.
  shipGateMode: VibeShipGateModeReadSchema.default(VIBE_SHIP_GATE_MODE_DEFAULT),
  errorReason: z.string().nullable(),
  /// Why the deployment failed, as a structured code the platform minted —
  /// the ONLY field the console classifies a failure on. `errorReason` above is
  /// the detail shown underneath and is never matched for a verdict: on a build
  /// failure it is the runner's raw output, which carries the tenant's own
  /// build log. Null when no code was recorded (a pre-code row, an executor
  /// that minted none, an operator's manual mark), which renders as the
  /// generic failure. Lenient for deploy skew: an older backend omits the key,
  /// a newer one may send a code this console does not know — both read null.
  reasonCode: VibeFailureReasonCodeReadSchema,
  /// The newer deployment that displaced this one, read from the
  /// `DEPLOYMENT_DISPLACED` audit row written in the same transaction as the
  /// flip to DISPLACED. Non-null only on a DISPLACED row — both producers (a
  /// newer commit superseding this one's build, and a newer deployment taking
  /// the rollout slot first) write that row. Null on every other status, and on
  /// a DISPLACED row whose audit row cannot be found — a client renders that as
  /// "a newer deployment", never as a guess.
  ///
  /// `.default(null)` for the same deploy-skew reason `shipGateMode` carries: a
  /// backend one release behind omits the key, and a required key would fail
  /// the whole deployments list rather than one field of it.
  displacedBy: VibeDeploymentDisplacerSchema.nullable().default(null),
  createdByUserId: z.string().nullable(),
  /// Display name of the user who triggered this deployment, joined at read
  /// time. Only populated on the list read (the console's Trigger column);
  /// null on single-deployment responses and when there is no actor.
  createdByName: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});

export type VibeDeploymentDto = z.infer<typeof VibeDeploymentSchema>;

// ----------------------------------------------------------------------------
// Trigger deployment
// ----------------------------------------------------------------------------

export const TriggerVibeDeploymentPathVarsSchema = z.object({
  appId: z.string().uuid()
});

export const TriggerVibeDeploymentBodySchema = z.object({
  /// The commit to deploy. OMIT it to deploy the head of the app's deploy
  /// branch — the platform resolves it from the ref the receive hook last
  /// advanced, which is the same head `GET /api/vibe/apps/:appId/refs`
  /// reports.
  ///
  /// Optional rather than a separate "deploy latest" route, and resolved
  /// server-side rather than by the caller, because "latest" has to mean one
  /// thing across the console, the CLI and every future client. A client that
  /// looks the sha up itself is a second definition, free to drift.
  ///
  /// 409 `VIBE_DEPLOYMENT_BRANCH_HEAD_MISSING` when omitted and nothing has
  /// been pushed to that branch — distinct from
  /// `VIBE_DEPLOYMENT_GIT_PROJECT_MISSING`, which means there is no
  /// repository at all.
  triggerSha: VibeTriggerShaSchema.optional(),
  /// The caller's explicit answer to the soft-limit spend question. Absent
  /// reads as false. When the org is over its usage soft cap the trigger
  /// responds `status: "confirmation_required"` instead of deploying; the
  /// caller re-sends the same request with this set to proceed. It is NOT
  /// an escape hatch out of an admin SUSPENDED block, which still 403s.
  confirmOverage: z.boolean().optional(),
  /// Build this sha AGAIN instead of reusing the image already in the
  /// registry. Absent reads as false.
  ///
  /// ECR is IMMUTABLE and the tag derives from the commit, so the first build
  /// of a sha permanently fixes its image — which makes an ordinary redeploy
  /// free, and made a sha built badly once impossible to correct. Setting this
  /// pushes to a tag suffixed with this deploy's own version (`…-v7`), so the
  /// rebuild lands somewhere nothing holds. It costs a full build; the default
  /// path deliberately does not.
  forceRebuild: z.boolean().optional(),
  /// Ship this commit even though the app requires verification. Absent reads
  /// as false. The server twin of the client hook's `NEXUS_GATE_ACK=1`.
  ///
  /// A DELIBERATE, ATTRIBUTED bypass, not a quiet one: when the app actually
  /// requires verification this writes a `DEPLOYMENT_VERIFICATION_OVERRIDDEN`
  /// audit row naming the overriding user and the commit, at trigger time —
  /// when the human made the decision. On an ungated app it changes nothing and
  /// writes nothing, so a count of those rows is a count of real bypasses
  /// rather than of flag usage.
  skipVerification: z.boolean().optional()
});

/**
 * The created arm: the deployment row, the sibling build-job row, AND —
 * when the owning app gates prod deploys — a PENDING approval request.
 * Inlining the approval request avoids a second round-trip for the CLI to
 * discover that the deploy is gated.
 */
export const TriggerVibeDeploymentCreatedSchema = z.object({
  status: z.literal("created"),
  deployment: VibeDeploymentSchema,
  buildJob: VibeBuildJobSchema,
  approvalRequest: VibeApprovalRequestSchema.nullable()
});

/**
 * The reuse arm: this app's newest deployment is ALREADY in flight for this
 * exact commit, so the trigger returns it instead of starting a second one.
 * Same shape as the created arm — the caller's deployment either way — with a
 * discriminator that does not claim a row was written.
 *
 * Why it exists. Two deployments of one commit do not run side by side,
 * because an app has exactly ONE rollout target: the desired-state projection
 * picks the most-recently-created row that is HEALTHY or DEPLOYING-with-an-
 * image, so the agent places only the NEWER twin. The older is never placed,
 * never reports healthy, and is reaped FAILED 15 minutes after entering
 * DEPLOYING. Measured on one tenant's app: of 21 push deployments twinned by a
 * CLI deploy within a minute, 19 FAILED and 0 succeeded, while the single
 * untwinned push SUPERSEDED normally. Every one of those failures reads on a
 * customer's board as their app failing to come up.
 *
 * Deliberately narrow. Only the NEWEST deployment is a reuse candidate, so a
 * caller who deploys A, then B, then A again gets a real third deployment —
 * they have asked for something after A and asked for A back. And
 * `forceRebuild` never reuses: that flag is the explicit request for a fresh
 * build of a commit already built.
 */
export const TriggerVibeDeploymentReusedSchema = z.object({
  status: z.literal("reused"),
  deployment: VibeDeploymentSchema,
  buildJob: VibeBuildJobSchema,
  approvalRequest: VibeApprovalRequestSchema.nullable()
});

/**
 * The confirmation arm: the org is over its usage SOFT cap, so nothing was
 * created and nothing was consumed (not even a version number). This is a
 * question, not a refusal — it rides a 2xx exactly like the created arm.
 *
 * The payload is deliberately minimal: it is everything the trigger already
 * knows from the cost-safety row it reads anyway, at zero extra cost. For
 * usage-vs-cap numbers to render a richer prompt, call
 * `GET /api/vibe/cost-safety` — it returns status, effective caps and
 * current-period usage per usage type.
 */
export const TriggerVibeDeploymentConfirmationRequiredSchema = z.object({
  status: z.literal("confirmation_required"),
  reason: z.object({
    costSafetyStatus: VibeCostSafetyStatusReadSchema,
    /// Human-readable rationale, renderable as-is.
    message: z.string()
  })
});

/**
 * Discriminated on `status` so a consumer cannot read a deployment that was
 * never created. ALL arms are 2xx — see the schemas above. `created` and
 * `reused` carry identical fields on purpose: a caller that only wants "my
 * deployment" reads `.deployment` off either without narrowing, and a caller
 * that cares whether it caused the deploy has the discriminator to check.
 */
export const TriggerVibeDeploymentResponseSchema = z.discriminatedUnion("status", [
  TriggerVibeDeploymentCreatedSchema,
  TriggerVibeDeploymentReusedSchema,
  TriggerVibeDeploymentConfirmationRequiredSchema
]);

/**
 * The trigger response as a READER parses it: a status a newer backend added
 * reads as the server's own word with every field it sent, so a CLI or console a
 * release behind reports "newer than this client" instead of a parse failure
 * after the deploy was already accepted.
 */
export const TriggerVibeDeploymentResponseReadSchema = z.union([
  TriggerVibeDeploymentResponseSchema,
  z.looseObject({ status: unlistedDiscriminantSchema(TriggerVibeDeploymentResponseSchema) })
]);

// ----------------------------------------------------------------------------
// Get deployment
// ----------------------------------------------------------------------------

export const GetVibeDeploymentPathVarsSchema = z.object({
  appId: z.string().uuid(),
  deploymentId: z.string().uuid()
});

export const GetVibeDeploymentResponseSchema = z.object({
  deployment: VibeDeploymentSchema,
  /// Nullable because legacy deployments (if any are ever backfilled)
  /// may not have a sibling build job. Current trigger flow guarantees
  /// one per deployment.
  buildJob: VibeBuildJobSchema.nullable()
});

// ----------------------------------------------------------------------------
// List deployments
// ----------------------------------------------------------------------------

export const ListVibeDeploymentsPathVarsSchema = z.object({
  appId: z.string().uuid()
});

export const ListVibeDeploymentsResponseSchema = z.object({
  deployments: z.array(VibeDeploymentSchema)
});
